import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Grid,
  Divider,
  Tooltip,
  Badge,
  Chip
} from '@mui/material';
import { ExpandMore, ExpandLess, Warning } from '@mui/icons-material';
import { SessionDetailsCube } from './session-details-cube';
import { K8sAnalyzerTopBarProps } from './k8s-analyzer-types';

export function K8sAnalyzerTopBar({
  deploymentTypeMap,
  sessionsWithoutPods,
  sessionsWithoutUsers,
  sessionStatusCounts,
  allSessions,
  pods
}: K8sAnalyzerTopBarProps) {
  const [expanded, setExpanded] = useState(true);
  const [showSessionsWithoutPods, setShowSessionsWithoutPods] = useState(true);
  const [showSessionsWithoutUsers, setShowSessionsWithoutUsers] = useState(true);
  const [expandedStatuses, setExpandedStatuses] = useState<Set<string>>(new Set());
  
  // Create a map of podId to nodeName for quick lookup
  const podNodeMap = useMemo(() => {
    const map = new Map<string, string>();
    pods.forEach(pod => {
      if (pod.name && pod.nodeName) {
        map.set(pod.name, pod.nodeName);
      }
    });
    return map;
  }, [pods]);

  // Enhance sessions with node information
  const enhanceSessionWithNode = useCallback((session: any) => {
    if (session.podId && podNodeMap.has(session.podId)) {
      return {
        ...session,
        nodeName: podNodeMap.get(session.podId)
      };
    }
    return session;
  }, [podNodeMap]);

  // Group sessions by status for easier display with node information
  const sessionsByStatus = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    
    allSessions.forEach(session => {
      if (session.status) {
        if (!grouped[session.status]) {
          grouped[session.status] = [];
        }
        grouped[session.status].push(enhanceSessionWithNode(session));
      }
    });
    
    return grouped;
  }, [allSessions, enhanceSessionWithNode]);

  // Enhance sessions without pods
  const enhancedSessionsWithoutPods = useMemo(() => {
    return sessionsWithoutPods.map(enhanceSessionWithNode);
  }, [sessionsWithoutPods, enhanceSessionWithNode]);

  // Enhance sessions without users
  const enhancedSessionsWithoutUsers = useMemo(() => {
    return sessionsWithoutUsers.map(enhanceSessionWithNode);
  }, [sessionsWithoutUsers, enhanceSessionWithNode]);

  // Initialize expanded statuses on component mount and when session data changes
  useEffect(() => {
    // Start with all statuses except 'connected'
    const initialExpandedStatuses = new Set<string>();
    
    Object.keys(sessionStatusCounts).forEach(status => {
      if (status !== 'connected' && sessionStatusCounts[status] > 0) {
        initialExpandedStatuses.add(status);
      }
    });
    
    setExpandedStatuses(initialExpandedStatuses);
  }, [sessionStatusCounts]);
  
  // Calculate total sessions
  const totalSessions = useMemo(() => {
    return Object.values(sessionStatusCounts).reduce((sum, count) => sum + count, 0);
  }, [sessionStatusCounts]);

  // Get the first (and only) deployment status since we know there's only one
  const deploymentStatus = Object.values(deploymentTypeMap)[0];

  if (!deploymentStatus) {
    return null;
  }

  const { count } = deploymentStatus;

  const getSessionStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'var(--bulk-success)'; // success green
      case 'qrcode':
        return '#2196f3'; // primary blue
      case 'initializing':
      case 'browser_started':
      case 'navigating':
      case 'unpaired':
        return 'var(--bulk-warning)'; // warning orange
      case 'disconnected':
        return 'var(--bulk-error)'; // error red
      default:
        return '#9e9e9e'; // default gray
    }
  };

  const hasOrphanedSessions = sessionsWithoutPods.length > 0 || sessionsWithoutUsers.length > 0;

  const toggleSessionStatus = (status: string) => {
    setExpandedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  return (
    <Paper
      sx={{
        mb: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}
      elevation={2}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div">
            Session Overview
          </Typography>
          
          {hasOrphanedSessions && (
            <Badge color="error" variant="dot" sx={{ mr: 1 }}>
              <Tooltip title="There are sessions with issues">
                <Warning color="warning" />
              </Tooltip>
            </Badge>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1">
            Total Pods: <strong>{count}</strong>
          </Typography>
          
          <Typography variant="body1">
            Total Sessions: <strong>{totalSessions}</strong>
          </Typography>
          
          {sessionStatusCounts.connected > 0 && (
            <Chip 
              label={`Connected: ${sessionStatusCounts.connected}`} 
              color="success" 
              size="small"
            />
          )}
          
          {sessionStatusCounts.disconnected > 0 && (
            <Chip 
              label={`Disconnected: ${sessionStatusCounts.disconnected}`} 
              color="error" 
              size="small"
            />
          )}
          
          <IconButton size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>Session Status</span>
                  <Tooltip title="Click on a status to toggle visibility">
                    <Typography variant="caption" color="text.secondary">
                      Click to toggle visibility
                    </Typography>
                  </Tooltip>
                </Box>
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(sessionStatusCounts).map(([status, count]) => (
                  <Grid item key={status}>
                    <Box 
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: expandedStatuses.has(status) ? getSessionStatusColor(status) : 'divider',
                        borderWidth: expandedStatuses.has(status) ? 2 : 1,
                        minWidth: 120,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSessionStatus(status);
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {status}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: getSessionStatusColor(status),
                          fontWeight: expandedStatuses.has(status) ? 'bold' : 'normal'
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
          
          {/* Session details by status */}
          {Object.entries(sessionsByStatus)
            .sort(([statusA], [statusB]) => {
              // Sort by priority: disconnected first, then others
              if (statusA === 'disconnected') return -1;
              if (statusB === 'disconnected') return 1;
              return statusA.localeCompare(statusB);
            })
            .map(([status, sessions]) => (
              <Collapse key={status} in={expandedStatuses.has(status)}>
                <Box sx={{ mt: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ 
                    color: getSessionStatusColor(status),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span>{status} Sessions ({sessions.length})</span>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSessionStatus(status);
                      }}
                    >
                      <ExpandLess />
                    </IconButton>
                  </Typography>
                  <Grid container spacing={1}>
                    {sessions.map(session => (
                      <Grid item key={session._id} xs={12} sm={6} md={4} lg={3}>
                        <SessionDetailsCube session={session} pods={pods} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Collapse>
            ))}
                      
          {/* Sessions without pods section */}
          {sessionsWithoutPods.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: showSessionsWithoutPods ? 'error.main' : 'divider',
                  pb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }
                }}
                onClick={() => setShowSessionsWithoutPods(!showSessionsWithoutPods)}
              >
                <Typography variant="subtitle1" color="error">
                  Sessions Without Pods ({sessionsWithoutPods.length})
                </Typography>
                <IconButton size="small">
                  {showSessionsWithoutPods ? <ExpandLess color="error" /> : <ExpandMore color="error" />}
                </IconButton>
              </Box>
              
              <Collapse in={showSessionsWithoutPods}>
                <Grid container spacing={1} sx={{ mt: 1, mb: 2 }}>
                  {enhancedSessionsWithoutPods.map(session => (
                    <Grid item key={session._id} xs={12} sm={6} md={4} lg={3}>
                      <SessionDetailsCube session={session} pods={pods} />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          )}
          
          {/* Sessions without users section */}
          {sessionsWithoutUsers.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: showSessionsWithoutUsers ? 'warning.main' : 'divider',
                  pb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }
                }}
                onClick={() => setShowSessionsWithoutUsers(!showSessionsWithoutUsers)}
              >
                <Typography variant="subtitle1" color="warning.main">
                  Sessions Without Users ({sessionsWithoutUsers.length})
                </Typography>
                <IconButton size="small">
                  {showSessionsWithoutUsers ? <ExpandLess color="warning" /> : <ExpandMore color="warning" />}
                </IconButton>
              </Box>
              
              <Collapse in={showSessionsWithoutUsers}>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {enhancedSessionsWithoutUsers.map(session => (
                    <Grid item key={session._id} xs={12} sm={6} md={4} lg={3}>
                      <SessionDetailsCube session={session} pods={pods} />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          )}

        </Box>
      </Collapse>
    </Paper>
  );
}

export default K8sAnalyzerTopBar; 